import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { conformToMask } from 'react-text-mask';
import { get } from 'lodash';
import MobileHeader from 'components/Header/MobileHeader';
import Input from 'components/Form/Input';
import { Button, ButtonLink } from 'components/Button';
import { PuffLoader } from 'react-spinners';
import Validator from 'components/Validator/Validator';
import {
  nextAction,
  checkPreviousAction,
} from 'actions/workflow';
import schema from './schema';
import style from './style.scss';

const phoneNumberMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

const ElectronicConsentModalBody = () => (
  <div className={style['terms-modal-body']}>
    <p>
      <b>CONSENT FOR ELECTRONIC SIGNATURES, RECORDS, AND DISCLOSURES (&quot;E-Consent&quot;)</b>
      <br />
      <br />
      Please read this information carefully and print a copy and/or retain this information electronically for future reference.
      <br />
      <br />
      <u><b>Introduction.</b></u> You are using this &apos;Portal&apos; or &apos;Website&apos; to submit a request for consumer financial services from Cross River Bank (the &quot;Bank&quot;), LendingUSA as operator of the Portal, and their assignees (hereinafter &quot;we,&quot; &quot;us,&quot; &quot;our,&quot; or &quot;Bank&quot;), among other services. To provide these services, we need your consent to using and accepting electronic signatures, records, and disclosures (&quot;E-Consent&quot;). This E-Consent notifies you of your rights when receiving disclosures, notices, and information from the Bank or LendingUSA. By clicking links or buttons assenting to our terms (&quot;Links&quot;), you agree that you received this E-Consent and that you consent to using electronic signatures, records, and disclosures. Additionally, by clicking Links, you consent to conduct transactions by using electronic disclosures, electronic records, and contract documents (&quot;Disclosures&quot;).
      <br />
      <br />
      <u>Option for Paper or Non-Electronic Records.</u> You may request any Disclosures in paper copy by logging in and printing a paper copy. You may also mail your written request to LendingUSA, at the following address: 15021 Ventura Blvd #879, Sherman Oaks, CA 91403. We will provide paper copies at no charge. The Bank or its assignee will retain all Disclosures as applicable law requires.
      <br />
      <br />
      <u><b>Scope of Consent.</b></u> This E-Consent applies to all interactions online concerning you and Bank and/or LendingUSA, and includes those interactions engaged in on any computer, electronic device, mobile device, including phones, smart-phones, fax machines, and tablets. Under this E-Consent, Bank and/or LendingUSA will process your information and interact during all online interactions with you electronically. The Disclosures you will receive electronically include federal disclosures, such as the Truth-in-Lending disclosures, Privacy Notices, and notices of our credit decisions, as well as state-specific notices. We will also send you notices electronically related to our interactions and transactions. This E-Consent informs you of your rights when receiving these Disclosures electronically.
      <br />
      <br />
      <u><b>Consenting to Do Business Electronically.</b></u> Before you decide to do business electronically with Bank and/or LendingUSA, you should consider whether you have the required hardware and software capabilities described below.
      <br />
      <br />
      <u><b>Hardware and Software Requirements.</b></u> To access and retain the Disclosures electronically, you will need to use a computer or device capable of accessing the Internet and an Internet Browser software program that supports at least 256-bit encryption, such as Microsoft® Internet Explorer 11+, Safari 7+ and the evergreen Chrome, Firefox or Edge. To read some documents, you may need a PDF file reader like Adobe® Acrobat Reader, Xpdf® or Foxit®. If these requirements change while you are maintaining an active relationship with Bank, and the change creates a material risk that you may not be able to receive Disclosures electronically, Bank will notify you of these changes. You will need a printer or a long-term storage device, such as your computer&apos;s disk drive, to retain a copy of the Disclosures for future reference. You may send us your written questions regarding the hardware and software requirements to:
      <br />
      <br />
      <b>Email</b>
      <br />
      <a href="mailto:CustomerCare@LendingUSA.com">CustomerCare@LendingUSA.com</a>
      <br />
      <br />
      <b>Call</b>
      <br />
      Our regular business hours are 9:00am to 5:00pm PST
      <br />
      800-994-6177
      <br />
      <br />
      <b>Address for Regular Mail</b>
      <br />
      15021 Ventura Blvd #879
      <br />
      Sherman Oaks, CA 91403
      <br />
      <br />
      <u><b>Withdrawing Consent.</b></u> You are free to withdraw this E-Consent at any time and at no charge. However, if you withdraw this E-Consent before receiving consumer financial services, this will prevent you from obtaining consumer financial services from us. If at any time you wish to withdraw this E-Consent, you can send us your written request by e-mail to CustomerCare@LendingUSA.com with the details of such request. If you decide to withdraw this E-Consent, the withdrawal will not affect the legal effectiveness, validity, and enforceability of prior electronic Disclosures.
      <br />
      <br />
      <u><b>Change to Your Contact Information.</b></u> You agree to keep us informed of any change in your electronic address or mailing address. You may update such information by calling us or emailing us to provide the updated information. You may also send us your written update by mail to our address above.
      <br />
      <br />
      <u><b>YOUR ABILITY TO ACCESS DISCLOSURES.</b></u> BY CLICKING LINKS, YOU ASSENT TO OUR TERMS. YOU ACKNOWLEDGE AND REPRESENT THAT YOU CAN ACCESS THE DISCLOSURES IN THE DESIGNATED FORMATS DESCRIBED ABOVE. Once you give your consent, you can log into the website to access these documents.
      <br />
      <br />
      <u><b>CONSENT.</b></u> BY CLICKING LINKS, YOU ASSENT TO OUR TERMS. YOU ACKNOWLEDGE YOU HAVE READ THIS INFORMATION ABOUT ELECTRONIC SIGNATURES, RECORDS, DISCLOSURES, AND DOING BUSINESS ELECTRONICALLY. YOU CONSENT TO USING ELECTRONIC SIGNATURES, HAVING ALL DISCLOSURES PROVIDED OR MADE AVAILABLE TO YOU IN ELECTRONIC FORM, AND TO DOING BUSINESS WITH US ELECTRONICALLY. YOU ACKNOWLEDGE THAT YOU MAY REQUEST A PAPER COPY OF THE ELECTRONIC RECORDS AND DISCLOSURES, WHICH WE WILL PROVIDE TO YOU AT NO CHARGE. IF YOU REFRAIN FROM PROCEEDING THEN YOU NEITHER WISH TO USE ELECTRONIC SIGNATURES NOR CONDUCT THIS TRANSACTION ELECTRONICALLY. YOU ALSO ACKNOWLEDGE THAT YOUR CONSENT TO ELECTRONIC DISCLOSURES IS REQUIRED TO RECEIVE CONSUMER FINANCIAL SERVICES FROM US OVER THE INTERNET.
      <br />
      <br />
      Print and retain a hard copy or electronic copy.
      <br />
      <br />
    </p>
    <p>
      <b>ELECTRONIC CONSENT AND ACKNOWLEDGMENT.</b> BY CLICKING LINKS, YOU CERTIFY THAT:
    </p>
    <ul>
      <li>Your computer hardware and software meet the requirements needed to access and retain the Disclosures electronically.</li>
      <li>You consent to receive the Disclosures electronically.</li>
      <li>You consent to sign your Loan Agreement and related documents electronically.</li>
      <li>You understand and agree that you will be bound to the same terms and conditions if you sign the Disclosures electronically as if you signed paper Disclosures.</li>
    </ul>
  </div>
);

const AppInvitationBumper = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const data = useSelector((state) => get(state, 'workflow.data.data'));
  const applicationId = useSelector((state) =>
    get(state, 'workflow.data.data.applicationId', '')
    || get(state, 'workflow.attributes.entityId', '')
  );
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckinLoading, setIsCheckinLoading] = useState(() => Boolean(key && !data));
  const [activeConsentModal, setActiveConsentModal] = useState(null);

  const navigateToGeneralError = (errorPayload) => {
    navigate('/fsl/general-error-page', {
      state: {
        data: errorPayload,
      },
    });
  };

  useEffect(() => {
    const { validator } = props;
    if (!key) {
      navigate('/fsl/application');
    } else if (key && data) {
      setIsCheckinLoading(false);
      const initialFormData = {
        firstName: get(data, 'firstName'),
        lastName: get(data, 'lastName'),
        fullName: get(data, 'firstName') + ' ' + get(data, 'lastName'),
        phoneNumber: conformToMask(get(data, 'phoneNumber'), phoneNumberMask).conformedValue,
      };
      validator.setValues(initialFormData);
    } else {
      setIsCheckinLoading(true);
      dispatch(checkPreviousAction({
        data: {
          isInvitation: true,
        },
        url: `/workflows/fsl/${key}/checkin`,
        success: (response) => {
          setIsCheckinLoading(false);
          const initialFormData = {
            firstName: get(response, 'data.data.firstName'),
            lastName: get(response, 'data.data.lastName'),
            fullName: get(response, 'data.data.firstName') + ' ' + get(response, 'data.data.lastName'),
            phoneNumber: conformToMask(get(response, 'data.data.phoneNumber'), phoneNumberMask).conformedValue,
          };
          validator.setValues(initialFormData);
        },
        fail: (error) => {
          setIsCheckinLoading(false);
          navigateToGeneralError(get(error, 'data'));
        },
      }));
    }
  }, []);

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleContinue = (e) => {
    e.preventDefault();

    if (key) {
      const { validator: { values, validate } } = props;
      if (validate(schema).isValid) {
        setIsLoading(true);
        setIsValid(true);
        const { fullName } = values;
        const [firstName, lastName] = fullName.split(' ');
        dispatch(nextAction({
          data: {
            firstName,
            lastName,
            phoneNumber: values.phoneNumber,
            consentToReceiveMessages: true,
            consentElectronicCommunication: true,
            isInvitation: true,
          },
          url: `/workflows/fsl/${key}/next`,
          success: (response) => {
            setIsLoading(false);
            const routeUrl = get(response, 'data.url');
            if (routeUrl) {
              navigate(routeUrl);
            } else {
              navigateToGeneralError(get(response, 'data'));
            }
          },
          fail: (error) => {
            setIsLoading(false);
            navigateToGeneralError(get(error, 'data'));
          },
        }));
      } else {
        validate(schema);
        setIsValid(false);
      }
    }
  };

  const { validator: { values, errors } } = props;

  const openTermsModal = (e) => {
    e.preventDefault();
    setActiveConsentModal('terms');
  };

  const openElectronicConsentModal = (e) => {
    e.preventDefault();
    setActiveConsentModal('electronic');
  };

  const closeConsentModal = () => {
    setActiveConsentModal(null);
  };

  return (
    <div className={style['app-invitation-page']}>
      <MobileHeader />
      <section className={style['page-content']}>
        <div className={style.card}>
          {isCheckinLoading ? (
            <div className={style['loader-wrap']} role="status" aria-live="polite">
              <PuffLoader color="#2457C5" size={40} />
            </div>
          ) : (
            <>
              {applicationId ? (
                <p className={style['application-id']}>
                  <b>Application ID:</b> {applicationId}
                </p>
              ) : null}
              <h1 className={style.title}>You&apos;ve Been Invited!</h1>
              <p className={style.subtitle}>
                You&apos;ve been invited to apply for a loan through LendingUSA&apos;s Freshstart program!
                To protect your privacy, please confirm your information and provide consent
                so we can securely take you to your application link.
              </p>

              <div className={style['verification-card']}>
                <h2 className={style['section-title']}>Verify Your Information</h2>
                <p className={style['section-copy']}>
                  For your security, please confirm your full name and phone number.
                </p>

                <div className={style['inputs-grid']}>
                  <Input
                    type="text"
                    name="fullName"
                    label="First and Last Name"
                    value={values.fullName}
                    onChange={handleInputChange}
                    isRequired
                    hasError={!!errors.fullName}
                    errorMessage={errors.fullName}
                    labelClassName={style['field-label']}
                    disabled
                  />
                  <Input
                    type="text"
                    name="phoneNumber"
                    isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                    value={conformToMask(values.phoneNumber, phoneNumberMask).conformedValue}
                    onChange={handleInputChange}
                    label="Phone Number"
                    isRequired
                    hasError={!!errors.phoneNumber}
                    errorMessage={errors.phoneNumber}
                    labelClassName={style['field-label']}
                  />
                </div>
              </div>

              {!isValid && (
                <div>
                  <p className={style['form-note']}>You must enter required and valid information before you can continue.</p>
                </div>
              )}

              <div className={style.consent}>
                <p>
                  By clicking &quot;Continue&quot;, I consent to receive calls and texts for any purpose,
                  including marketing, under these{' '}
                  <ButtonLink
                    type="button"
                    onClick={openTermsModal}
                    className={style['terms-link']}
                  >
                    terms
                  </ButtonLink>
                  .
                </p>
                <p>
                  By clicking &quot;Continue&quot;, I also agree to the{' '}
                  <ButtonLink
                    type="button"
                    onClick={openElectronicConsentModal}
                    className={style['terms-link']}
                  >
                    electronic consent
                  </ButtonLink>
                  .
                </p>
              </div>

              <Button
                className={cn('button large green w-100', style.button, isLoading ? '' : 'arrow')}
                isLoading={isLoading}
                isDisabled={isLoading}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </>
          )}
        </div>
      </section>
      <div className={style['footer-text']}>
        <p>All loans are made by our <a href="https://lendingusa.com/lending-partners/" target="_blank" rel="noreferrer">lending partners</a>. Amounts financed range from $1,000 to $35,000. Loans are not available in every state. An origination fee of up to 8% may be included in the principal loan amount that results in an APR up to 35.99%. Loan approvals are subject to credit underwriting and identity, income and/or employment verification.</p>
      </div>

      {activeConsentModal ? (
        <>
          <div
            className={style['terms-modal-backdrop']}
            aria-hidden="true"
            onClick={closeConsentModal}
          />
          <div
            className={style['terms-modal']}
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-invitation-consent-modal-title"
          >
            <div className={style['terms-modal-header']}>
              <h5 id="app-invitation-consent-modal-title">
                {activeConsentModal === 'terms' ? 'Terms' : 'Electronic Consent'}
              </h5>
              <button
                className={style['terms-modal-close-x']}
                type="button"
                aria-label="Close"
                onClick={closeConsentModal}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            {activeConsentModal === 'terms' ? (
              <div className={style['terms-modal-body']}>
                <p>
                  By providing the telephone number above and clicking &quot;Check My Rate&quot;, you confirm you are the owner of the phone number(s), or that the owner authorized you to provide the phone number(s), or that you are the regular user of this phone number(s). You are providing express written consent via your electronic signature to receive any type of communication for any purpose, including telemarketing communications, from our lending partners, LendingUSA, their agents, representatives, affiliates, assignees or anyone calling on their behalf, at the telephone number(s) provided for any lawful purpose, even if that number is on a federal or state do-not-call list. The scope of this consent includes, but is not limited to, receiving live operator calls, calls and texts via an automatic telephone dialing system (auto-dialer), prerecorded and artificial voice messages, text/SMS messages or other means. These telephone calls and text messages may incur message, data and access fees from your telephone provider. Consent is not a condition to obtain a loan.
                  You understand that you can request a paper copy of these terms or may withdraw your consent at any time by:
                </p>
                <ul>
                  <li>mailing your written request to LendingUSA c/o Compliance Department at 15021 Ventura Blvd #879, Sherman Oaks, CA 91403;</li>
                  <li>by e-mailing <a href="mailto:customercare@lendingusa.com">customercare@lendingusa.com</a>; or</li>
                  <li>by calling LendingUSA at (800) 994-6177.</li>
                </ul>
                <p>
                  You may also reply &quot;STOP&quot; to opt out of receiving autodialed text messages only.
                </p>
              </div>
            ) : (
              <ElectronicConsentModalBody />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

AppInvitationBumper.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default Validator(schema)(AppInvitationBumper);
